import * as React from "react";
import { ChevronDownIcon, ChevronUpIcon, XIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { IconButton } from "./icon-button";
import {
  Sheet,
  SheetContent,
  type SheetContentProps,
  SheetDescription,
  SheetTitle,
} from "./sheet";

/** Slide-over (or ≥1280px split) detail container: header bar, scrollable body, property sidebar. */

export type DetailPaneSize = "sm" | "md" | "lg";

export interface DetailPaneProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rendered as the accessible dialog title in the header bar. */
  title: React.ReactNode;
  /** Optional breadcrumb rendered before the title. */
  breadcrumb?: React.ReactNode;
  /** 384px (`sm`), 520px (`md`), 640px (`lg`). Default `md`. */
  size?: DetailPaneSize;
  /** `split` docks the pane inline at ≥1280px and falls back to an overlay below. */
  mode?: "overlay" | "split";
  /** Previous item (↑ / k). */
  onPrev?: () => void;
  /** Next item (↓ / j). */
  onNext?: () => void;
  /** Right-aligned header actions. */
  actions?: React.ReactNode;
  /** `PropertyRow` stack; shown from `lg` up. */
  sidebar?: React.ReactNode;
  /** Screen-reader description of the pane contents. */
  description?: string;
  /**
   * Escape hatch onto the underlying Radix dialog content, for the dismissal
   * handlers a pane sometimes has to intercept — most often
   * `onPointerDownOutside` / `onInteractOutside`, so a click inside a portalled
   * Popover, Select or DatePicker does not dismiss the pane behind it.
   *
   * Applies to the overlay form only; the docked (`split`) form is not a
   * dialog and has nothing outside to dismiss on. `className` and `children`
   * are owned by `DetailPane` and cannot be overridden here.
   */
  contentProps?: Omit<SheetContentProps, "className" | "children" | "side" | "size" | "hideClose">;
  className?: string;
  /** Main body. */
  children: React.ReactNode;
}

const splitWidth: Record<DetailPaneSize, string> = {
  sm: "w-96",
  md: "w-pane",
  lg: "w-pane-lg",
};

/** Tracks a media query without pulling in extra state plumbing. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

function DetailPaneHeader({
  title,
  breadcrumb,
  onPrev,
  onNext,
  actions,
  onClose,
  asDialogTitle,
}: {
  title: React.ReactNode;
  breadcrumb?: React.ReactNode;
  onPrev?: () => void;
  onNext?: () => void;
  actions?: React.ReactNode;
  onClose: () => void;
  asDialogTitle: boolean;
}) {
  const titleClassName = "min-w-0 truncate text-sm font-semibold text-text";

  return (
    <div className="flex h-header shrink-0 items-center gap-1 px-4">
      <IconButton
        label="Close"
        size="sm"
        shortcut="esc"
        onClick={onClose}
        tooltipSide="bottom"
      >
        <XIcon />
      </IconButton>
      {(onPrev || onNext) && (
        <div className="flex shrink-0 items-center">
          <IconButton
            label="Previous"
            size="sm"
            shortcut="k"
            disabled={!onPrev}
            onClick={onPrev}
          >
            <ChevronUpIcon />
          </IconButton>
          <IconButton
            label="Next"
            size="sm"
            shortcut="j"
            disabled={!onNext}
            onClick={onNext}
          >
            <ChevronDownIcon />
          </IconButton>
        </div>
      )}
      {breadcrumb ? (
        <div className="flex min-w-0 shrink items-center gap-1 text-xs text-text-tertiary">
          {breadcrumb}
        </div>
      ) : null}
      {asDialogTitle ? (
        <SheetTitle className={titleClassName}>{title}</SheetTitle>
      ) : (
        <h2 className={titleClassName}>{title}</h2>
      )}
      {actions ? (
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

function DetailPaneBody({
  sidebar,
  children,
}: {
  sidebar?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1">
      <div
        data-slot="detail-pane-body"
        className="min-w-0 flex-1 space-y-4 overflow-y-auto px-4 py-3"
      >
        {children}
      </div>
      {/* No rule between body and rail (`docs/borderless-contract.md` §1): the
          combined `px-4` + `px-4` gutter and the rail's `text-xs` property-row
          type are the separation. */}
      {sidebar ? (
        <div
          data-slot="detail-pane-properties"
          // w-64, not w-56: with a 112px label column, 224px left ~100px for
          // the value, so ordinary strings ("Not calculated") and dates were
          // truncating in a column whose whole job is showing them.
          className="hidden w-64 shrink-0 space-y-1 overflow-y-auto border-l border-line px-4 py-3 lg:block"
        >
          {sidebar}
        </div>
      ) : null}
    </div>
  );
}

const DetailPane = React.forwardRef<HTMLElement, DetailPaneProps>(
  (
    {
      open,
      onOpenChange,
      title,
      breadcrumb,
      size = "md",
      mode = "overlay",
      onPrev,
      onNext,
      actions,
      sidebar,
      description,
      contentProps,
      className,
      children,
    },
    ref,
  ) => {
    const isWide = useMediaQuery("(min-width: 1280px)");
    const useSplit = mode === "split" && isWide;
    const close = React.useCallback(() => onOpenChange(false), [onOpenChange]);

    // The overlay form is a Radix dialog, so Esc / focus trap / focus restore
    // come for free. The docked form is non-modal, so it wires Esc itself.
    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key !== "Escape") return;
        event.stopPropagation();
        close();
      },
      [close],
    );

    if (useSplit) {
      if (!open) return null;
      return (
        <aside
          ref={ref}
          data-slot="detail-pane"
          data-mode="split"
          aria-label={typeof title === "string" ? title : "Details"}
          onKeyDown={handleKeyDown}
          className={cn(
            // Docked: the pane is on the canvas tier beside the `bg-panel` page
            // sheet, so the surface step separates it — no edge (§1).
            "flex h-full shrink-0 flex-col bg-page",
            splitWidth[size],
            className,
          )}
        >
          <DetailPaneHeader
            title={title}
            breadcrumb={breadcrumb}
            onPrev={onPrev}
            onNext={onNext}
            actions={actions}
            onClose={close}
            asDialogTitle={false}
          />
          <DetailPaneBody sidebar={sidebar}>{children}</DetailPaneBody>
        </aside>
      );
    }

    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          ref={ref as React.Ref<HTMLDivElement>}
          data-slot="detail-pane"
          data-mode="overlay"
          side="right"
          size={size}
          hideClose
          {...contentProps}
          className={cn("bg-page", className)}
        >
          <SheetDescription className="sr-only">
            {description ?? "Detail panel"}
          </SheetDescription>
          <DetailPaneHeader
            title={title}
            breadcrumb={breadcrumb}
            onPrev={onPrev}
            onNext={onNext}
            actions={actions}
            onClose={close}
            asDialogTitle
          />
          <DetailPaneBody sidebar={sidebar}>{children}</DetailPaneBody>
        </SheetContent>
      </Sheet>
    );
  },
);
DetailPane.displayName = "DetailPane";

export { DetailPane };
